import React from 'react';

/**
 * Button — Rascal EPR Design System v2 primitive.
 *
 * Variants (DESIGN.md §4):
 *  - primary   : in-app actions (stone-900, never brand-navy)
 *  - secondary : alternative actions
 *  - submit    : commit / send / final actions (emerald-700, taller)
 *  - tertiary  : low-emphasis ("← Back", "Skip")
 *
 * All variants meet 44 px tap-target minimum on mobile.
 * Disabled state automatically applied; no transform animations.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'submit' | 'tertiary';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  /** Material Symbol Rounded name (e.g. "send", "check") */
  icon?: string;
  iconPosition?: 'leading' | 'trailing';
  loading?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'h-11 px-5 bg-stone-900 text-white text-base font-medium rounded-xl hover:bg-stone-800 active:bg-stone-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-900',
  secondary:
    'h-11 px-5 bg-white border border-stone-300 text-stone-800 text-base font-medium rounded-xl hover:border-stone-500 active:border-stone-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-500',
  submit:
    'h-12 px-6 bg-emerald-700 text-white text-base font-semibold rounded-xl hover:bg-emerald-800 active:bg-emerald-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700',
  tertiary:
    'h-9 px-3 text-sm text-stone-600 hover:text-stone-900 active:text-stone-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-500 rounded-md',
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  fullWidth = false,
  icon,
  iconPosition = 'leading',
  loading = false,
  className = '',
  children,
  disabled,
  type = 'button',
  ...rest
}) => {
  const base =
    'inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:pointer-events-none whitespace-nowrap';
  const widthCls = fullWidth ? 'w-full' : '';
  const composed = `${base} ${VARIANT_CLASSES[variant]} ${widthCls} ${className}`.trim();

  return (
    <button
      type={type}
      className={composed}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="material-symbols-rounded animate-spin" style={{ fontSize: 18 }} aria-hidden="true">
          progress_activity
        </span>
      ) : (
        icon && iconPosition === 'leading' && (
          <span className="material-symbols-rounded" style={{ fontSize: 18 }} aria-hidden="true">
            {icon}
          </span>
        )
      )}
      {children}
      {!loading && icon && iconPosition === 'trailing' && (
        <span className="material-symbols-rounded" style={{ fontSize: 18 }} aria-hidden="true">
          {icon}
        </span>
      )}
    </button>
  );
};

export default Button;
